/**
 * ReMind critters: the app's own avatar pack. One soft pebble body, drawn in
 * the bridge palette with an ink outline, dressed per avatar id. Replaces the
 * stock emoji avatars so every character looks like it lives in the same world.
 */

import { CRITTERS, type Ears, type Face, type Look, type Mouth, type Top } from './critters';

const INK = '#2f261e';

function EarsShape({ ears, body, fill }: { ears: Ears; body: string; fill?: string }) {
  const s = { stroke: INK, strokeWidth: 2.2, strokeLinejoin: 'round' as const };
  switch (ears) {
    case 'cat':
      return (<g {...s}><path d="M15 24 L17 8 L28 17 Z" fill={body} /><path d="M49 24 L47 8 L36 17 Z" fill={body} /><path d="M18 19 L19 12 L24 16 Z" fill={fill ?? body} strokeWidth={0} /><path d="M46 19 L45 12 L40 16 Z" fill={fill ?? body} strokeWidth={0} /></g>);
    case 'fox':
      return (<g {...s}><path d="M13 26 L14 6 L29 17 Z" fill={body} /><path d="M51 26 L50 6 L35 17 Z" fill={body} /><path d="M17 20 L17 12 L23 17 Z" fill="#fbefe0" strokeWidth={0} /><path d="M47 20 L47 12 L41 17 Z" fill="#fbefe0" strokeWidth={0} /></g>);
    case 'bear':
      return (<g {...s}><circle cx="17" cy="17" r="7" fill={body} /><circle cx="47" cy="17" r="7" fill={body} /><circle cx="17" cy="17" r="3" fill={fill ?? body} strokeWidth={0} /><circle cx="47" cy="17" r="3" fill={fill ?? body} strokeWidth={0} /></g>);
    case 'panda':
      return (<g {...s}><circle cx="17" cy="17" r="7" fill="#33404f" /><circle cx="47" cy="17" r="7" fill="#33404f" /></g>);
    case 'koala':
      return (<g {...s}><circle cx="13" cy="22" r="10" fill={body} /><circle cx="51" cy="22" r="10" fill={body} /><circle cx="13" cy="22" r="5" fill={fill} strokeWidth={0} /><circle cx="51" cy="22" r="5" fill={fill} strokeWidth={0} /></g>);
    case 'bunny':
      return (<g {...s}><ellipse cx="24" cy="10" rx="5" ry="12" fill={body} /><ellipse cx="40" cy="10" rx="5" ry="12" fill={body} /></g>);
    case 'floppy':
      return (<g {...s}><path d="M14 20 C6 22 6 38 12 42 C16 40 18 30 20 24 Z" fill={fill} /><path d="M50 20 C58 22 58 38 52 42 C48 40 46 30 44 24 Z" fill={fill} /></g>);
    case 'tufts':
      return (<g {...s}><path d="M16 22 L14 10 L24 16 Z" fill={body} /><path d="M48 22 L50 10 L40 16 Z" fill={body} /></g>);
    case 'elf':
      return (<g {...s}><path d="M12 34 L2 24 L14 28 Z" fill={body} /><path d="M52 34 L62 24 L50 28 Z" fill={body} /></g>);
    default:
      return null;
  }
}

function TopShape({ top }: { top: Top }) {
  const s = { stroke: INK, strokeWidth: 2.2, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  switch (top) {
    case 'antenna':
      return (<g {...s}><path d="M32 14 V5" fill="none" /><circle cx="32" cy="5" r="3.5" fill="#f0a43a" /></g>);
    case 'horn':
      return (<g {...s}><path d="M28 15 L32 0 L36 15 Z" fill="#f6d36e" /><path d="M30 10 L34 8 M29.5 13 L34.5 11" fill="none" strokeWidth={1.2} /></g>);
    case 'crown':
      return (<g {...s}><path d="M20 16 L21 4 L27 10 L32 2 L37 10 L43 4 L44 16 Z" fill="#f0a43a" /><circle cx="32" cy="9" r="1.6" fill="#d1683f" strokeWidth={0} /></g>);
    case 'tiara':
      return (<g {...s}><path d="M22 16 Q32 4 42 16" fill="none" stroke="#f0a43a" strokeWidth={3} /><circle cx="32" cy="8" r="3" fill="#8fcbe6" /></g>);
    case 'wizard':
      return (<g {...s}><path d="M16 18 Q32 22 48 18 L36 -2 Z" fill="#4a5a8a" /><path d="M14 18 Q32 24 50 18" fill="none" /><path d="M33 8 l1.2 2.4 2.6 .4 -1.9 1.8 .5 2.6 -2.4 -1.3 -2.3 1.3 .4 -2.6 -1.9 -1.8 2.7 -.4 z" fill="#f6d36e" strokeWidth={1} /></g>);
    case 'fedora':
      return (<g {...s}><path d="M10 19 Q32 25 54 19 Q48 15 44 16 L42 6 Q32 2 22 6 L20 16 Q16 15 10 19 Z" fill="#6b5240" /><path d="M21 13 Q32 16 43 13" fill="none" stroke="#2f261e" strokeWidth={3} /></g>);
    case 'leaf':
      return (<g {...s}><path d="M18 18 Q30 2 48 8 Q44 18 18 18 Z" fill="#7fb487" /><path d="M22 16 Q34 10 44 10" fill="none" strokeWidth={1.2} /></g>);
    case 'spikes':
      return (<g {...s}><path d="M20 16 L23 6 L27 14 L32 3 L37 14 L41 6 L44 16 Z" fill="#f0a43a" /></g>);
    case 'sprout':
      return (<g {...s}><path d="M32 15 V7" fill="none" /><path d="M32 9 Q24 2 20 8 Q26 12 32 9 Z" fill="#8fb98a" /><path d="M32 7 Q40 0 44 6 Q38 10 32 7 Z" fill="#8fb98a" /></g>);
    default:
      return null;
  }
}

function FaceShape({ face, look }: { face: Face; look: Look }) {
  const ink = look.body === '#3b3631' || look.body === '#33404f' ? '#fbf6ec' : INK;
  switch (face) {
    case 'happy':
      return (<g fill="none" stroke={ink} strokeWidth={2.2} strokeLinecap="round"><path d="M22 35 q3 -4 6 0" /><path d="M36 35 q3 -4 6 0" /></g>);
    case 'sleepy':
      return (<g fill="none" stroke={ink} strokeWidth={2.2} strokeLinecap="round"><path d="M22 35 q3 3 6 0" /><path d="M36 35 q3 3 6 0" /></g>);
    case 'big':
      return (<g><circle cx="25" cy="34" r="5" fill="#fbf6ec" stroke={INK} strokeWidth={2} /><circle cx="39" cy="34" r="5" fill="#fbf6ec" stroke={INK} strokeWidth={2} /><circle cx="26" cy="35" r="2.2" fill={INK} /><circle cx="40" cy="35" r="2.2" fill={INK} /></g>);
    case 'mask':
      return (<g><rect x="12" y="29" width="40" height="11" rx="5.5" fill="#f3e6d0" /><circle cx="25" cy="34.5" r="2.4" fill={INK} /><circle cx="39" cy="34.5" r="2.4" fill={INK} /><path d="M52 33 l7 -3 -2 6 z" fill="#d1683f" /></g>);
    case 'hero':
      return (<g><path d="M17 31 Q32 27 47 31 Q47 39 41 39 Q36 39 32 35 Q28 39 23 39 Q17 39 17 31 Z" fill={look.cape ?? '#d1683f'} stroke={INK} strokeWidth={2} /><circle cx="25" cy="34" r="2.2" fill="#fbf6ec" /><circle cx="39" cy="34" r="2.2" fill="#fbf6ec" /></g>);
    case 'sockets':
      return (<g fill="#3b3631"><ellipse cx="25" cy="34" rx="5" ry="5.5" /><ellipse cx="39" cy="34" rx="5" ry="5.5" /><circle cx="26.5" cy="33" r="1.4" fill="#fbf6ec" /><circle cx="40.5" cy="33" r="1.4" fill="#fbf6ec" /></g>);
    case 'patches':
      return (<g><ellipse cx="24" cy="35" rx="6" ry="7" fill="#33404f" transform="rotate(-20 24 35)" /><ellipse cx="40" cy="35" rx="6" ry="7" fill="#33404f" transform="rotate(20 40 35)" /><circle cx="25" cy="34" r="1.8" fill="#fbf6ec" /><circle cx="39" cy="34" r="1.8" fill="#fbf6ec" /></g>);
    default:
      return (<g fill={ink}><circle cx="25" cy="34" r="2.6" /><circle cx="39" cy="34" r="2.6" /><circle cx="26" cy="33" r=".9" fill="#fbf6ec" /><circle cx="40" cy="33" r=".9" fill="#fbf6ec" /></g>);
  }
}

function MouthShape({ mouth, look }: { mouth: Mouth; look: Look }) {
  const ink = look.body === '#3b3631' || look.body === '#33404f' ? '#fbf6ec' : INK;
  const s = { fill: 'none', stroke: ink, strokeWidth: 2, strokeLinecap: 'round' as const };
  switch (mouth) {
    case 'beak':
      return <path d="M29 40 L35 40 L32 45 Z" fill="#f0a43a" stroke={INK} strokeWidth={1.6} strokeLinejoin="round" />;
    case 'snout':
      return (<g><ellipse cx="32" cy="43" rx="7" ry="5" fill={look.belly ?? '#fbefe0'} stroke={INK} strokeWidth={1.8} /><ellipse cx="32" cy="41.5" rx="2.4" ry="1.7" fill={INK} /><path d="M32 43 v2" {...s} stroke={INK} strokeWidth={1.4} /></g>);
    case 'fang':
      return (<g><path d="M27 42 q5 4 10 0" {...s} /><path d="M29 43 l1 3 1 -2.5" fill="#fbf6ec" stroke={INK} strokeWidth={1.2} /></g>);
    case 'stitch':
      return (<g {...s}><path d="M25 43 H39" /><path d="M28 41 v4 M32 41 v4 M36 41 v4" strokeWidth={1.4} /></g>);
    case 'o':
      return <ellipse cx="32" cy="43" rx="3" ry="3.6" fill={INK} />;
    case 'grin':
      return <path d="M26 41 Q32 47 38 41 Z" fill={INK} stroke={INK} strokeWidth={1.6} strokeLinejoin="round" />;
    default:
      return <path d="M28 42 q4 4 8 0" {...s} />;
  }
}

interface Props {
  id?: string | null;
  size?: number;
  className?: string;
  /** Position when nested inside another SVG. */
  x?: number;
  y?: number;
  title?: string;
  /** Blink every few seconds (the living critter on the home scene). */
  blink?: boolean;
  /** Something to wear for the weather or the hour. */
  wear?: Wear | null;
  /** Eyes closed in a happy squint (joy) or asleep (night). */
  expression?: 'joy' | 'sleep' | null;
}

export type Wear = 'umbrella' | 'sunglasses' | 'scarf' | 'nightcap' | 'partyhat' | 'snack';

function WearShape({ wear }: { wear: Wear }) {
  const s = { stroke: INK, strokeWidth: 2, strokeLinejoin: 'round' as const, strokeLinecap: 'round' as const };
  switch (wear) {
    case 'umbrella':
      return (
        <g {...s}>
          <path d="M52 52 V6" fill="none" />
          <path d="M52 56 q0 3 -3 3" fill="none" />
          <path d="M30 10 Q52 -12 74 10 Q68 6 63 10 Q57 5 52 10 Q47 5 41 10 Q36 6 30 10 Z" fill="#d1683f" />
        </g>
      );
    case 'sunglasses':
      return (
        <g {...s}>
          <path d="M18 31 H46" fill="none" />
          <rect x="18" y="30" width="12" height="8" rx="4" fill="#2f261e" />
          <rect x="34" y="30" width="12" height="8" rx="4" fill="#2f261e" />
          <path d="M21 32 l3 0" stroke="#fbf6ec" strokeWidth={1.4} />
        </g>
      );
    case 'scarf':
      return (
        <g {...s}>
          <path d="M14 48 Q32 56 50 48 L50 53 Q32 61 14 53 Z" fill="#2e6b60" />
          <path d="M40 53 L44 64 L38 63 L35 55" fill="#2e6b60" />
        </g>
      );
    case 'partyhat':
      return (
        <g {...s}>
          <path d="M22 16 L32 -6 L42 16 Z" fill="#f0a43a" />
          <path d="M25 9 L39 9 M28 2 L36 2" stroke="#d1683f" strokeWidth={2.4} />
          <circle cx="32" cy="-7" r="3.5" fill="#d1683f" />
        </g>
      );
    case 'snack':
      return (
        <g {...s}>
          <circle cx="56" cy="46" r="7" fill="#d1683f" />
          <path d="M56 39 q1 -4 4 -5" fill="none" />
          <path d="M57 38 q4 -3 6 0 q-3 2 -6 0" fill="#2e6b60" strokeWidth={1.4} />
        </g>
      );
    case 'nightcap':
      return (
        <g {...s}>
          <path d="M16 20 Q32 10 48 20 Q46 6 30 4 Q20 4 10 14 Z" fill="#4a5a8a" />
          <circle cx="9" cy="15" r="4" fill="#fbf6ec" />
        </g>
      );
  }
}

export default function Critter({ id, size = 48, className, x, y, title, blink, wear, expression }: Props) {
  const look = CRITTERS[id ?? 'default'] ?? CRITTERS.default;
  const shape = look.shape ?? 'pebble';
  const bodyPath =
    shape === 'ghost'
      ? 'M12 40 C12 22 20 14 32 14 C44 14 52 22 52 40 V56 l-5 -4 -5 4 -5 -4 -5 4 -5 -4 -5 4 -5 -4 -5 4 Z'
      : shape === 'tall'
        ? 'M32 8 C44 14 48 28 48 42 C48 52 42 56 32 56 C22 56 16 52 16 42 C16 28 20 14 32 8 Z'
        : 'M32 14 C47 14 54 25 54 38 C54 51 45 58 32 58 C19 58 10 51 10 38 C10 25 17 14 32 14 Z';

  return (
    <svg
      width={size}
      height={size}
      x={x}
      y={y}
      viewBox="0 0 64 64"
      className={className}
      role={title ? 'img' : undefined}
      aria-label={title}
      aria-hidden={title ? undefined : true}
      overflow="visible"
    >
      {look.cape && <path d="M14 46 Q8 58 18 62 H46 Q56 58 50 46 Z" fill={look.cape} stroke={INK} strokeWidth={2.2} strokeLinejoin="round" />}
      {look.mane && (
        <g fill={look.mane} stroke={INK} strokeWidth={2}>
          {Array.from({ length: 12 }).map((_, i) => {
            const a = (i / 12) * Math.PI * 2;
            return <circle key={i} cx={32 + Math.cos(a) * 22} cy={37 + Math.sin(a) * 21} r={7} />;
          })}
        </g>
      )}
      {look.flame && <path d="M26 54 Q32 66 38 54 Z" fill="#f0a43a" stroke={INK} strokeWidth={2} strokeLinejoin="round" />}
      {shape === 'tall' && (
        <g fill="#d1683f" stroke={INK} strokeWidth={2} strokeLinejoin="round">
          <path d="M17 40 L8 52 L18 50 Z" /><path d="M47 40 L56 52 L46 50 Z" />
        </g>
      )}
      <EarsShape ears={look.ears} body={look.body} fill={look.earFill} />
      <path d={bodyPath} fill={look.body} stroke={INK} strokeWidth={2.4} strokeLinejoin="round" />
      {look.belly && <ellipse cx="32" cy="47" rx="13" ry="9" fill={look.belly} />}
      {look.top === 'helmet' && <circle cx="32" cy="36" r="27" fill="#bfe3f2" fillOpacity="0.35" stroke={INK} strokeWidth={2.2} />}
      <TopShape top={look.top} />
      {expression ? (
        <g fill="none" stroke={look.body === '#3b3631' || look.body === '#33404f' ? '#fbf6ec' : INK} strokeWidth={2.2} strokeLinecap="round">
          {expression === 'joy'
            ? <><path d="M21 35 q4 -5 8 0" /><path d="M35 35 q4 -5 8 0" /></>
            : <><path d="M21 34 q4 3 8 0" /><path d="M35 34 q4 3 8 0" /></>}
        </g>
      ) : (
        <g className={blink && !['mask', 'hero', 'sockets'].includes(look.face) ? 'critter-blink' : undefined}>
          <FaceShape face={look.face} look={look} />
        </g>
      )}
      <MouthShape mouth={look.mouth} look={look} />
      {look.face !== 'mask' && look.face !== 'sockets' && look.face !== 'patches' && (
        <g fill="#e48b74" opacity="0.55"><ellipse cx="18" cy="41" rx="3.2" ry="2" /><ellipse cx="46" cy="41" rx="3.2" ry="2" /></g>
      )}
      {wear && <WearShape wear={wear} />}
      {look.mouth === 'stitch' && look.body === '#b6cf9a' && <path d="M44 22 l4 4 M46 21 l-1 6" stroke={INK} strokeWidth={1.4} strokeLinecap="round" />}
    </svg>
  );
}
